import { Box, Button, Checkbox, Divider, FormLabel, Pagination, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { ChangeEvent, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import equipmentApi from '~/apis/equipment';
import locationApi from '~/apis/location';
import utilityApi from '~/apis/utility';
import { ActivatedIcon, AddNewIcon, ExportFillIcon, ImportFillIcon } from '~/assets/icons';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { cast, clearInputFilter, exportExcel, handleApiError, notify } from '~/utils/helper';
import { IEquipment, IGetAllParams } from '~/utils/interface/equipment';
import { IMasterDataItem } from '~/utils/interface/master';
import DetailEquipmentDialog from './component/detail-equipment-dialog/DetailEquipmentDialog';
import EditEquipmentDialog from './component/edit-equipment-dialog/EditEquipmentDialog';
import EquipmentTable from './component/EquipmentTable';
import styles from './Equipment.module.scss';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import ImportEquipmentDialog from './component/import-equipment-dialog/ImportEquipmentDialog';
import { useAppDispatch } from '~/redux/hooks';
import { commonActions } from '~/thunks/common/commonSlice';

const cx = classNames.bind(styles);

const initialValues: IGetAllParams = {
  device_code: '',
  device_name: '',
  sim_no: '',
  province_code: [],
  province: '',
  ward_code: [],
  ward: '',
  address: '',
  status: [],
  sim_status: [],
  iot_status: [],
  agent: [],
  agent_name: '',
  distributor: [],
  distributor_name: '',
  owner: '',
  expire_date_from: '',
  expire_date_to: '',
  order_by: {
    field: '',
    type: '',
  },
  page_index: 1,
  page_size: 10,
};

const Equipment = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('admin/equipment');
  const [equipments, setEquipments] = useState<IEquipment[]>([]);
  const [nppOptions, setNppOptions] = useState<IMasterDataItem[]>([]);
  const [agentOptions, setAgentOptions] = useState<IMasterDataItem[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<IMasterDataItem[]>([]);
  const [wardOptions, setWardOptions] = useState<IMasterDataItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const statusOptions = [
    {
      id: '0',
      name: t('status.notActivated'),
    },
    {
      id: '1',
      name: t('status.valid'),
    },
    {
      id: '2',
      name: t('status.dueDate'),
    },
    {
      id: '3',
      name: t('status.overDate'),
    },
  ];

  const connectionsOptions = [
    {
      id: '0',
      name: t('connectionStatusOptions.disconnected'),
    },
    {
      id: '1',
      name: t('connectionStatusOptions.connected'),
    },
  ];

  const activeOptions = [
    { id: 0, name: t('active.0') },
    { id: 1, name: t('active.1') },
  ];

  const [isOpenDetailEquipmentDialog, setIsOpenDetailEquipmentDialog] = useState<boolean>(false);
  const [isOpenEditEquipmentDialog, setIsOpenEditEquipmentDialog] = useState<boolean>(false);
  const [isOpenDeleteEquipmentDialog, setIsOpenDeleteEquipmentDialog] = useState<boolean>(false);
  const [equipment, setEquipment] = useState<IEquipment>();
  const [editEquipmentDialogMode, setEditEquipmentDialogMode] = useState<string>(DIALOG_MODE_CREATE);
  const [refreshData, setRefreshData] = useState(false);
  const [isOpenImportEquipmentDialog, setIsOpenImportEquipmentDialog] = useState<boolean>(false);
  const [equipmentIds, setEquipmentIds] = useState<Array<number>>([]);

  useEffect(() => {
    if (refreshData) {
      handleGetListEquipment();
      setRefreshData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshData]);

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getListDistributors();
        setNppOptions(
          response.data.map((item) => {
            return {
              id: item.username,
              name: item.username + ' - ' + item.name,
              data0: item.name,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
    (async () => {
      try {
        const response = await utilityApi.getListAgents();
        setAgentOptions(
          response.data.map((item, index) => {
            return {
              id: item.username,
              name: item.username + ' - ' + item.name,
              data0: item.name,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
    (async () => {
      try {
        const response = await locationApi.getListProvinces();
        setProvinceOptions(
          response.data.map((item) => {
            return {
              id: item.code,
              name: item.code + ' - ' + item.name,
              data0: item.name,
              data1: item.name_with_type,
              data2: item.type,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
  }, []);

  const form = useFormik<IGetAllParams>({
    initialValues,
    onSubmit: async () => {
      handleGetListEquipment();
    },
  });

  useEffect(() => {
    handleGetListEquipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.page_index, form.values.page_size, form.values?.order_by?.field, form.values?.order_by?.type]);

  useEffect(() => {
    form.setFieldValue('page_index', 1);
    form.handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.values.iot_status,
    form.values.sim_status,
    form.values.distributor,
    form.values.agent,
    form.values.province_code,
    form.values.ward_code,
    form.values.status,
    form.values.active,
    form.values.expire_date_from,
    form.values.expire_date_to,
    form.values.address,
    form.values.ward,
    form.values.province,
    form.values.sim_no,
    form.values.device_code,
    form.values.device_name,
    form.values.distributor_name,
    form.values.agent_name,
  ]);

  useEffect(() => {
    if (form.values.province_code?.length) {
      (async () => {
        try {
          // const response = await locationApi.getListWards({
          //   province: form.values.province_code?.length ? form.values.province_code.join(',') : '',
          // });
          // setWardOptions(
          //   response.data.map((item) => {
          //     return {
          //       id: item.code,
          //       name: item.code + ' - ' + item.name,
          //       data0: item.name,
          //       data1: item.name_with_type,
          //       data2: item.type,
          //       data3: item.parent_code,
          //     };
          //   })
          // );
        } catch (error) {
          setWardOptions([]);
          handleApiError(error);
        }
      })();
    } else setWardOptions([]);
  }, [form.values.province_code]);

  const getListEquipment = async (overrideParams = {}) => {
    try {
      const sim_status =
        form.values.sim_status?.map((item) => {
          return Number(item);
        }) ?? [];
      const iot_status =
        form.values.iot_status?.map((item) => {
          return Number(item);
        }) ?? [];
      const status =
        form.values.status?.map((item) => {
          return Number(item);
        }) ?? [];
      return (
        await equipmentApi.getOverviewData({
          ...form.values,
          sim_status: sim_status,
          iot_status: iot_status,
          status: status,
          ...overrideParams,
        })
      ).data;
    } catch (error) {
      handleApiError(error);
      return { total: 0, devices: [] };
    }
  };

  const handleGetListEquipment = async () => {
    const response = await getListEquipment();

    setTotalItems(response.total);
    setEquipments(response.devices);
    if (equipment) {
      // Refresh selected equipment data
      setEquipment(response.devices.find((item) => item.device_id === equipment.device_id));
    }
  };

  const getStatus = (value: string) => {
    if (Number(value) === 0) {
      return (
        <Box className='text-[#72777B] border border-[#72777B] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.notActivated')}
        </Box>
      );
    } else if (Number(value) === 3) {
      return (
        <Box className='text-[#F32A4C] border border-[#F32A4C] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.overDate')}
        </Box>
      );
    } else if (Number(value) === 2) {
      return (
        <Box className='text-[#FF9500] border border-[#FF9500] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.dueDate')}
        </Box>
      );
    } else {
      return (
        <Box className='text-[#3D9149] border border-[#3D9149] rounded-[20px] py-[3px] px-[15px]'>
          {t('status.valid')}
        </Box>
      );
    }
  };
  const getConnectionStatusStyle = (value: string) => {
    if (Number(value) === 0) {
      return <Typography className='text-[#F32A4C]'>{t('connectionStatusOptions.disconnected')}</Typography>;
    } else if (Number(value) === 1) {
      return <Typography className='text-[#3D9149]'>{t('connectionStatusOptions.connected')}</Typography>;
    } else if (Number(value) === 2) {
      return <Typography className='text-[#72777B]'>{t('connectionStatusOptions.error')}</Typography>;
    }
  };
  const renderStatusOptionItem = (option: { id: string; name: string }, _index: number) => {
    const isChecked = !form.values?.iot_status?.length
      ? false
      : form.values?.iot_status?.map((item) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full flex'>
        <Checkbox checked={isChecked} className='mr-[7px]' />
        <Box className={`text-center flex-1 flex justify-center`}>{getStatus(option.id)}</Box>
      </Box>
    );
  };

  const renderSimOptionItem = (option: { id: string; name: string }, _index: number) => {
    const isChecked = !form.values?.sim_status?.length
      ? false
      : form.values?.sim_status?.map((item) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full flex'>
        <Checkbox checked={isChecked} className='mr-[7px]' />
        <Box className={`text-center flex-1 flex justify-center`}>{getStatus(option.id)}</Box>
      </Box>
    );
  };

  const renderConnectionOptionItem = (option: { id: string; name: string }, _index: number) => {
    const isChecked = !form.values?.status?.length
      ? false
      : form.values?.status?.map((item) => Number(item))?.indexOf(Number(option.id)) > -1;

    return (
      <Box key={option.id} className='select-option w-full flex'>
        <Checkbox checked={isChecked} className='mr-[7px]' />
        <Box className={`text-center flex-1 flex justify-center`}>{getConnectionStatusStyle(option.id)}</Box>
      </Box>
    );
  };

  const handleSetIsOpenEditEquipmentDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenEditEquipmentDialog(isOpen);
    if (needRefresh) {
      handleGetListEquipment();
    }
  };

  const handleDeleteDevice = async (needRefresh: boolean = false) => {
    try {
      if (equipment) {
        await equipmentApi.deleteDevice(equipment?.device_id);
        if (needRefresh) {
          handleGetListEquipment();
        }
        notify('success', t('deleteEquipmentDialog.deleteSuccess'));
        setIsOpenDeleteEquipmentDialog(false);
      }
    } catch (error) {
      handleApiError(error);
    }
  };

  const exportEquipments = async () => {
    if (!equipmentIds.length) return notify('error', t('emptyExportFile'));
    const response = await getListEquipment({
      page_index: 1,
      page_size: totalItems,
    });

    let res = response.devices;
    if (equipmentIds.length) {
      const filterSet = new Set(equipmentIds);
      res = response.devices?.filter((item) => filterSet.has(cast(item.device_id)));
      if (!res.length) {
        setEquipmentIds([]);
        res = response.devices;
      }
    }
    const columns = [
      { header: t('exportFile.no'), key: 'no' },
      { header: t('exportFile.deviceCode'), key: 'device_code' },
      { header: t('exportFile.deviceName'), key: 'device_name' },
      { header: t('exportFile.province'), key: 'province' },
      { header: t('exportFile.ward'), key: 'ward' },
      { header: t('exportFile.address'), key: 'address' },
      { header: t('exportFile.installationLocation'), key: 'installation_location' },
      { header: t('exportFile.ownerName'), key: 'owner_name' },
      { header: t('exportFile.owner'), key: 'owner' },
      { header: t('exportFile.connectStatus'), key: 'connect_status' },
      { header: t('exportFile.simNo'), key: 'sim_no' },
      { header: t('exportFile.simStatus'), key: 'sim_status' },
      { header: t('exportFile.simActiveDate'), key: 'sim_active_date' },
      { header: t('exportFile.simExpireDate'), key: 'sim_expire_date' },
      { header: t('exportFile.iotStatus'), key: 'iot_status' },
      { header: t('exportFile.expireDate'), key: 'expire_date' },
      { header: t('exportFile.agent'), key: 'agent' },
      { header: t('exportFile.distributor'), key: 'distributor' },
      { header: t('exportFile.supplier'), key: 'supplier' },
      { header: t('exportFile.description'), key: 'description' },
      { header: t('exportFile.createdAt'), key: 'created_at' },
      { header: t('exportFile.createdBy'), key: 'created_by' },
      { header: t('exportFile.updatedAt'), key: 'updated_at' },
      { header: t('exportFile.lastdataAt'), key: 'lastdata_at' },
      { header: t('exportFile.lastconnectAt'), key: 'lastconnect_at' },
    ];

    const data = res?.map((device, index) => ({
      ...device,
      no: index + 1,
      connect_status: t(`sensorStatusLabel.status.${device.connect_status}`),
      sim_status: t(`simStatuses.${device.sim_status}`),
      iot_status: t(`simStatuses.${device.iot_status}`),
    }));

    exportExcel(columns, data, t('exportFile.fileName'));
  };

  const handleSetIsOpenImportEquipmentDialog = (isOpen: boolean = false, needRefresh: boolean = false) => {
    setIsOpenImportEquipmentDialog(isOpen);
    if (needRefresh) handleGetListEquipment();
  };

  const handleCheckAll = async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      const res = await getListEquipment({ page_index: 1, page_size: totalItems });
      setEquipmentIds(res.devices?.map((equipment: IEquipment) => cast(equipment.device_id)));
    } else {
      setEquipmentIds([]);
    }
  };

  const handleCheck = async (row: IEquipment, checked: boolean) => {
    if (checked) {
      setEquipmentIds((prev) => [...(prev ?? []), ...[cast(row.device_id)]]);
    } else {
      setEquipmentIds(equipmentIds.filter((user) => user !== row.device_id));
    }
  };

  const handleActiveDevices = async () => {
    dispatch(commonActions.setIsLoading(true));
    try {
      await equipmentApi.activateDevices({
        deviceIds: equipmentIds,
        active: 1,
      });
      notify('success', t('activatedMessageSuccess'));
      setEquipmentIds([]);
      setRefreshData(true);
    } catch (error) {
      handleApiError(error);
    } finally {
      dispatch(commonActions.setIsLoading(false));
    }
  };

  return (
    <Box className={cx('w-full', 'equipment-screen')}>
      <Box className={cx('box-container')}>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='distributor'>{t('npp')}</FormLabel>
            <BaseSelect
              options={nppOptions}
              value={form.values.distributor}
              placeholder={t('select')}
              name='distributor'
              isSearchEnable
              multiple
              onChange={(e) => form.setFieldValue('distributor', e.target.value)}
              selectAllOptions={(data) => form.setFieldValue('distributor', data)}
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='agent'>{t('agency')}</FormLabel>
            <BaseSelect
              options={agentOptions}
              value={form.values.agent}
              placeholder={t('select')}
              name='agent'
              isSearchEnable
              multiple
              onChange={(e) => form.setFieldValue('agent', e.target.value)}
              selectAllOptions={(data) => form.setFieldValue('agent', data)}
            />
          </Box>
        </Box>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='province_code'>{t('province')}</FormLabel>
            <BaseSelect
              options={provinceOptions}
              value={form.values.province_code}
              placeholder={t('select')}
              name='province_code'
              isSearchEnable
              multiple
              onChange={(e) => form.setFieldValue('province_code', e.target.value)}
              selectAllOptions={(data) => form.setFieldValue('province_code', data)}
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='ward_code'>{t('ward')}</FormLabel>
            <BaseSelect
              options={wardOptions}
              value={form.values.ward_code}
              placeholder={t('select')}
              name='ward_code'
              isSearchEnable
              multiple
              onChange={(e) => form.setFieldValue('ward_code', e.target.value)}
              selectAllOptions={(data) => form.setFieldValue('ward_code', data)}
            />
          </Box>
        </Box>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='iot_status'>{t('iotStatus')}</FormLabel>
            <BaseSelect
              options={statusOptions}
              value={form.values.iot_status ?? []}
              placeholder={t('select')}
              name='iot_status'
              isSearchEnable
              onChange={(e) => {
                form.setFieldValue('iot_status', e.target.value);
              }}
              multiple
              selectAllOptions={(data) => {
                form.setFieldValue('iot_status', data);
              }}
              renderOptionItem={renderStatusOptionItem}
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='sim_status'>{t('simStatus')}</FormLabel>
            <BaseSelect
              options={statusOptions}
              value={form.values.sim_status ?? []}
              placeholder={t('select')}
              name='sim_status'
              isSearchEnable
              onChange={(e) => {
                form.setFieldValue('sim_status', e.target.value);
              }}
              multiple
              selectAllOptions={(data) => {
                form.setFieldValue('sim_status', data);
              }}
              renderOptionItem={renderSimOptionItem}
            />
          </Box>
        </Box>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='status'>{t('connectionStatus')}</FormLabel>
            <BaseSelect
              options={connectionsOptions}
              value={form.values.status ?? []}
              placeholder={t('select')}
              name='status'
              isSearchEnable
              onChange={(e) => {
                form.setFieldValue('status', e.target.value);
              }}
              multiple
              selectAllOptions={(data) => {
                form.setFieldValue('status', data);
              }}
              renderOptionItem={renderConnectionOptionItem}
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='active'>{t('activeStatus')}</FormLabel>
            <BaseSelect
              options={activeOptions}
              value={form.values.active}
              placeholder={t('select')}
              name='active'
              onChange={form.handleChange}
              displayInvalidValue
              isDisplayAllOption
            />
          </Box>
        </Box>
        <Box className={cx('column-text-field')}>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='expire_date_from'>{t('start')}</FormLabel>
            <BaseDatePicker
              portalId='datePicker-portalId'
              value={form.values.expire_date_from}
              dateFormat='YYYY-MM-DD'
              onChange={(date) => {
                form.setFieldValue('expire_date_from', date);
              }}
              placeholder={t('select')}
              calendarProps={form.values.expire_date_to ? { maxDate: new Date(form.values.expire_date_to) } : {}}
            />
          </Box>
          <Box className={cx('row-text-field')}>
            <FormLabel htmlFor='expire_date_to'>{t('end')}</FormLabel>
            <BaseDatePicker
              portalId='datePicker-portalId'
              value={form.values.expire_date_to}
              dateFormat='YYYY-MM-DD'
              onChange={(date) => {
                form.setFieldValue('expire_date_to', dayjs(date).format('YYYY-MM-DD'));
              }}
              placeholder={t('select')}
              calendarProps={form.values.expire_date_from ? { minDate: new Date(form.values.expire_date_from) } : {}}
            />
          </Box>
        </Box>
        <Box className='column-text-field'>
          <Button
            className='bg-[#ED1002] rounded-[20px] text-white w-[130px] h-[35px] hover:bg-[#ED1002] hover:text-white hover:opacity-80'
            onClick={() => {
              clearInputFilter();
              form.resetForm();
            }}
          >
            {t('clearFilter')}
          </Button>
        </Box>
      </Box>
      <Divider
        sx={{
          borderColor: '#005F73',
          borderBottomWidth: 1,
          width: '100%',
          my: '14px',
        }}
      ></Divider>
      <Box className={cx('table-container')}>
        <Box className='bg-[#ED1002] flex py-[2px] px-5 justify-between items-center'>
          <Typography className='text-[16px] font-bold text-[white]'>{t('equipmentList')}</Typography>
          <Box className='flex pr-10'>
            <Button startIcon={<ActivatedIcon />} className='text-white font-semibold' onClick={handleActiveDevices}>
              {t('activated')}
            </Button>
            <Button
              startIcon={<AddNewIcon />}
              className='text-white font-semibold'
              onClick={() => {
                setEditEquipmentDialogMode(DIALOG_MODE_CREATE);
                handleSetIsOpenEditEquipmentDialog(true);
              }}
            >
              {t('addNew')}
            </Button>
            <Button
              startIcon={<ImportFillIcon />}
              className='text-white font-semibold'
              onClick={() => setIsOpenImportEquipmentDialog(true)}
            >
              {t('fileImport')}
            </Button>
            <Button startIcon={<ExportFillIcon />} className='text-white font-semibold' onClick={exportEquipments}>
              {t('fileExport')}
            </Button>
          </Box>
        </Box>
        <EquipmentTable
          pageSize={form.values.page_size ?? 10}
          equipments={equipments}
          form={form}
          onEdit={(row) => {
            setEquipment(row);
            setEditEquipmentDialogMode(DIALOG_MODE_EDIT);
            handleSetIsOpenEditEquipmentDialog(true);
          }}
          onDetail={(row) => {
            setEquipment(row);
            setIsOpenDetailEquipmentDialog(true);
          }}
          onDelete={(row) => {
            setEquipment(row);
            setIsOpenDeleteEquipmentDialog(true);
          }}
          setRefreshData={setRefreshData}
          equipmentIds={equipmentIds}
          handleCheck={handleCheck}
          handleCheckAll={handleCheckAll}
        />
      </Box>
      <Box className={cx('flex items-center justify-end gap-10 mt-5', 'pagination-box')}>
        <Typography>
          {t('sum')}: <span className='font-bold text-[#005f73]'>{totalItems}</span> {t('equipment')}
        </Typography>
        <Box className='w-[150px]'>
          <BaseSelect
            options={[
              { id: 10, name: '10' },
              { id: 20, name: '20' },
              { id: 50, name: '50' },
              { id: 100, name: '100' },
              { id: 250, name: '250' },
              { id: 500, name: '500' },
            ]}
            value={form.values.page_size}
            onChange={(e) => {
              form.setFieldValue('page_size', e.target.value);
              form.setFieldValue('page_index', 1);
            }}
            renderSelected={(option: any) => (
              <Typography className='px-2'>
                {option.name}/{t('page').toLowerCase()}
              </Typography>
            )}
          />
        </Box>
        <Pagination
          count={Math.max(1, Math.ceil(totalItems / (form.values.page_size ?? 1)))}
          variant='outlined'
          page={form.values.page_index ?? 1}
          onChange={(e, value: number) => {
            form.setFieldValue('page_index', value);
          }}
        />
        <Box className='flex items-center gap-3'>
          <Typography>{t('page')}</Typography>
          <Box className='rounded-[20px] border border-[#ED1002] text-center py-2 px-7'>{form.values.page_index}</Box>
        </Box>
      </Box>
      <EditEquipmentDialog
        open={isOpenEditEquipmentDialog}
        mode={editEquipmentDialogMode}
        setOpen={handleSetIsOpenEditEquipmentDialog}
        data={equipment}
      />
      <DetailEquipmentDialog
        open={isOpenDetailEquipmentDialog}
        setOpen={setIsOpenDetailEquipmentDialog}
        data={equipment}
      />
      <ConfirmDialog
        open={isOpenDeleteEquipmentDialog}
        title={t('deleteEquipmentDialog.title')}
        description={t('deleteEquipmentDialog.description', {
          deviceCode: equipment?.device_code,
        })}
        onConfirm={() => handleDeleteDevice(true)}
        onCancel={() => setIsOpenDeleteEquipmentDialog(false)}
      />
      <ImportEquipmentDialog open={isOpenImportEquipmentDialog} setOpen={handleSetIsOpenImportEquipmentDialog} />
    </Box>
  );
};

export default memo(Equipment);
